/**
 * Servicio de sesión OIDC: login/logout, claims del JWT y token para el backend.
 */
import { Injectable, inject, signal } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { resolveOidcDiscoveryError } from '../../../core/http/resolve-http-error-message';
import { environment } from '../../../../environments/environment';
import { buildAuthConfig } from '../auth.config';
import {
  AUTH_RETURN_URL_STORAGE_KEY,
  AUTH_SESSION_STORAGE_KEY,
  DEV_AUTH_BEARER_TOKEN,
  DEV_USUARIO_ID,
} from '../constants/auth-session.constants';
import { DASHBOARD_ROUTE } from '../models/nav-item.model';
import { PermisosService } from './permisos.service';

export interface SessionUser {
  sub: string;
  email: string | null;
  nombre: string;
  keycloakGroups: string[];
}

interface DevStoredSession {
  kind: 'dev';
  establishedAt: number;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly oauthService = inject(OAuthService);
  private readonly devBypassActive = signal(environment.authDevBypass);
  private readonly devSessionActive = signal(false);
  /** Evita re-configurar OIDC en cada activación de guard (borra el token en memoria). */
  private initPromise: Promise<void> | null = null;
  /** Un solo refresh en vuelo: varios 401 concurrentes esperan la misma Promise (rotación Entra). */
  private refreshInFlight: Promise<boolean> | null = null;
  /** Mensaje de error al cargar discovery document / OIDC; no lanza excepción al bootstrap. */
  private readonly oidcInitError = signal<string | null>(null);

  constructor() {
    this.restoreDevSessionFromStorage();
  }

  /** Inicializa el cliente OIDC (PKCE) o restaura sesión dev desde sessionStorage. */
  async init(): Promise<void> {
    if (this.devBypassActive()) {
      this.restoreDevSessionFromStorage();
      return;
    }

    if (!this.initPromise) {
      this.initPromise = this.initOidcSession();
    }

    try {
      await this.initPromise;
      this.oidcInitError.set(null);
    } catch (error) {
      this.initPromise = null;
      this.oidcInitError.set(resolveOidcDiscoveryError(error));
    }
  }

  /** Error al inicializar OIDC (p. ej. Keycloak caído o mal configurado). */
  getOidcInitError(): string | null {
    return this.oidcInitError();
  }

  /** Limpia el error OIDC antes de reintentar login. */
  clearOidcInitError(): void {
    this.oidcInitError.set(null);
    this.initPromise = null;
  }

  private async initOidcSession(): Promise<void> {
    this.oauthService.configure(buildAuthConfig());
    await this.oauthService.loadDiscoveryDocumentAndTryLogin();
    if (this.oauthService.hasValidAccessToken()) {
      this.oauthService.setupAutomaticSilentRefresh();
    }
  }

  /**
   * Intenta renovar el access token con el refresh grant (single-flight).
   * @param options.forceRefresh Si true, no se salta el refresh aunque el
   *   access token local aún parezca vigente (p. ej. 401 del backend).
   * @returns `true` si hay un token usable tras el intento.
   */
  async ensureValidAccessToken(options?: { forceRefresh?: boolean }): Promise<boolean> {
    if (this.devBypassActive()) {
      return this.devSessionActive();
    }

    if (!options?.forceRefresh && this.oauthService.hasValidAccessToken()) {
      return true;
    }

    if (!this.refreshInFlight) {
      this.refreshInFlight = this.renewAccessToken().finally(() => {
        this.refreshInFlight = null;
      });
    }

    return this.refreshInFlight;
  }

  /**
   * Renueva el access token vía refresh grant. Sin refresh token, o si el
   * grant falla, no usa iframe (`silentRefresh`).
   * @returns `true` si el access token quedó válido.
   */
  private async renewAccessToken(): Promise<boolean> {
    if (!this.oauthService.getRefreshToken()) {
      return this.oauthService.hasValidAccessToken();
    }

    try {
      await this.oauthService.refreshToken();
    } catch {
      return false;
    }

    if (this.oauthService.hasValidAccessToken()) {
      this.oauthService.setupAutomaticSilentRefresh();
      return true;
    }

    return false;
  }

  /** Limpia sesión local expirada sin redirigir al logout de Keycloak. */
  clearExpiredSession(): void {
    this.logout({ localOnly: true });
    this.initPromise = null;
  }

  isDevBypass(): boolean {
    return this.devBypassActive();
  }

  isAuthenticated(): boolean {
    if (this.devBypassActive()) {
      return this.devSessionActive();
    }
    return this.oauthService.hasValidAccessToken();
  }

  /**
   * Completa el login: en dev establece sesión local; en producción inicia flujo OIDC.
   * @returns `true` si la sesión quedó lista en la app (modo dev); `false` si redirige al IdP.
   */
  completeLogin(returnUrl?: string | null): boolean {
    if (this.devBypassActive()) {
      this.establishDevSession();
      return true;
    }
    if (this.oidcInitError()) {
      return false;
    }
    this.startOidcLogin(returnUrl);
    return false;
  }

  /** Redirige al proveedor OIDC (Authorization Code + PKCE). */
  login(returnUrl?: string | null): void {
    if (this.devBypassActive()) {
      this.establishDevSession();
      return;
    }
    if (this.oidcInitError()) {
      return;
    }
    this.startOidcLogin(returnUrl);
  }

  /**
   * Cierra sesión local y, si aplica, en Keycloak (end-session).
   * @param options.localOnly Si true, no redirige al logout de Keycloak (errores de API/sesión).
   */
  logout(options?: { localOnly?: boolean }): void {
    this.refreshInFlight = null;
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(AUTH_SESSION_STORAGE_KEY);
    }
    this.devSessionActive.set(false);

    try {
      inject(PermisosService).clear();
    } catch {
      // Entorno de test sin árbol de inyección completo.
    }

    if (this.devBypassActive()) {
      return;
    }

    if (options?.localOnly) {
      // Evita el bounce Keycloak → / → login cuando falla permisos/me.
      this.oauthService.logOut(true);
      return;
    }

    try {
      this.oauthService.logOut();
    } catch {
      this.oauthService.logOut(true);
    }
  }

  /**
   * Bearer para APIs Colmena.
   * En Entra, el `access_token` suele ser de Microsoft Graph (`aud` Graph / `iss` sts.windows.net);
   * el backend espera el `id_token` (aud = SPA, iss = login.microsoftonline.com/.../v2.0).
   */
  getAccessToken(): string | null {
    if (!this.isAuthenticated()) {
      return null;
    }
    if (this.devBypassActive()) {
      return environment.devAuthToken ?? DEV_AUTH_BEARER_TOKEN;
    }
    const accessToken = this.oauthService.getAccessToken() || null;
    const idToken = this.oauthService.getIdToken() || null;
    if (this.shouldPreferIdTokenForApi(accessToken)) {
      return idToken || accessToken;
    }
    return accessToken || idToken;
  }

  /**
   * Prefiere id_token cuando el access_token no es válido como audience de nuestras APIs
   * (p. ej. token Graph de Entra sin scope de API propio).
   */
  private shouldPreferIdTokenForApi(accessToken: string | null): boolean {
    if (!accessToken || !accessToken.includes('.')) {
      return true;
    }
    try {
      const payloadJson = accessToken.split('.')[1] ?? '';
      const normalized = payloadJson.replace(/-/g, '+').replace(/_/g, '/');
      const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
      const payload = JSON.parse(atob(padded)) as { aud?: string | string[]; iss?: string };
      const audiences = Array.isArray(payload.aud)
        ? payload.aud.map(String)
        : payload.aud
          ? [String(payload.aud)]
          : [];
      const graphAudience = '00000003-0000-0000-c000-000000000000';
      if (audiences.includes(graphAudience)) {
        return true;
      }
      const iss = String(payload.iss ?? '');
      if (iss.includes('sts.windows.net')) {
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  getSessionUser(): SessionUser | null {
    if (!this.isAuthenticated()) {
      return null;
    }

    if (this.devBypassActive()) {
      return {
        sub: DEV_USUARIO_ID,
        email: 'dev@matriz.cl',
        nombre: 'Usuario desarrollo',
        keycloakGroups: ['APP_MR_ADMIN'],
      };
    }

    const claims = this.oauthService.getIdentityClaims() as Record<string, unknown> | null;
    if (!claims) {
      return null;
    }

    const sub = String(claims['sub'] ?? '');
    const email = claims['email'] ? String(claims['email']) : null;
    const nombre =
      String(claims['name'] ?? claims['preferred_username'] ?? email ?? 'Usuario').trim() ||
      'Usuario';
    const keycloakGroups = this.resolveKeycloakGroups(claims);

    return { sub, email, nombre, keycloakGroups };
  }

  getUserInitials(nombre: string): string {
    const parts = nombre.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
      return 'US';
    }
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }
    return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
  }

  private establishDevSession(): void {
    const payload: DevStoredSession = {
      kind: 'dev',
      establishedAt: Date.now(),
    };
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(AUTH_SESSION_STORAGE_KEY, JSON.stringify(payload));
    }
    this.devSessionActive.set(true);
  }

  consumePostLoginReturnUrl(): string | null {
    const fromState = this.sanitizeReturnUrl(this.oauthService.state);
    if (fromState) {
      this.clearStoredReturnUrl();
      return fromState;
    }

    if (typeof sessionStorage === 'undefined') {
      return null;
    }
    const stored = this.sanitizeReturnUrl(sessionStorage.getItem(AUTH_RETURN_URL_STORAGE_KEY));
    this.clearStoredReturnUrl();
    return stored;
  }

  private restoreDevSessionFromStorage(): void {
    if (!this.devBypassActive() || typeof sessionStorage === 'undefined') {
      return;
    }
    try {
      const raw = sessionStorage.getItem(AUTH_SESSION_STORAGE_KEY);
      if (!raw) {
        this.devSessionActive.set(false);
        return;
      }
      const parsed = JSON.parse(raw) as DevStoredSession;
      this.devSessionActive.set(parsed.kind === 'dev');
    } catch {
      sessionStorage.removeItem(AUTH_SESSION_STORAGE_KEY);
      this.devSessionActive.set(false);
    }
  }

  private startOidcLogin(returnUrl?: string | null): void {
    const target = this.sanitizeReturnUrl(returnUrl) ?? DASHBOARD_ROUTE;
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(AUTH_RETURN_URL_STORAGE_KEY, target);
    }
    this.oauthService.initCodeFlow(target);
  }

  private clearStoredReturnUrl(): void {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(AUTH_RETURN_URL_STORAGE_KEY);
    }
  }

  private sanitizeReturnUrl(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }
    const trimmed = value.trim();
    if (!trimmed.startsWith('/')) {
      return null;
    }
    return trimmed || DASHBOARD_ROUTE;
  }

  private resolveKeycloakGroups(claims: Record<string, unknown>): string[] {
    const fromId = this.extractGroups(claims['groups']);
    if (fromId.length > 0) {
      return fromId;
    }
    const accessToken = this.oauthService.getAccessToken();
    if (!accessToken) {
      return [];
    }
    try {
      const payloadPart = accessToken.split('.')[1];
      if (!payloadPart) {
        return [];
      }
      const payload = JSON.parse(atob(payloadPart)) as Record<string, unknown>;
      return this.extractGroups(payload['groups']);
    } catch {
      return [];
    }
  }

  private extractGroups(groupsClaim: unknown): string[] {
    if (!groupsClaim) {
      return [];
    }
    const raw = Array.isArray(groupsClaim)
      ? groupsClaim.map((g) => String(g))
      : [String(groupsClaim)];
    return raw.map((group) => this.normalizeGroupName(group)).filter(Boolean);
  }

  /** Keycloak suele enviar `/APP_MR_ADMIN` o `/realm/APP_MR_ADMIN`; el mapa usa `APP_MR_*`. */
  private normalizeGroupName(group: string): string {
    const trimmed = group.trim();
    if (!trimmed) {
      return '';
    }
    const segments = trimmed.split('/').filter(Boolean);
    return segments[segments.length - 1] ?? trimmed;
  }
}
